import React, { useCallback, useEffect, useState } from "react";
import { Button, CheckPicker } from "rsuite";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import ReportShowHideColumnModal from "./ReportShowHideColumnModal";
import { getRatePlansByProperty, getRoomsByProperty } from "../../api";
import { Can, P } from "../../rbac";
import { UI_LABELS } from "./utils";
import FilterIcon from "../../assets/img/filter.svg";

const ResReportFilters = ({
  propertyId,
  isUploadSidePanelOpen,
  isOffline,
  setIsUploadSidePanelOpen,
  demandClients,
  promoRatePlans,
  exportReservation,
  isExportLoading,
  setColumns,
  setFilters,
  timeZoneId,
  isPilotHotel = false,
}) => {
  const [rateplans, setRateplans] = useState([]);
  const [roomtypes, setRoomtypes] = useState([]);
  const [selectedDemandClients, setSelectedDemandClients] = useState([]);
  const [selectedRateplans, setSelectedRateplans] = useState([]);
  const [selectedRoomtypes, setSelectedRoomtypes] = useState([]);
  const [selectedPromoRateplans, setSelectedPromoRateplans] = useState([]);
  const [isShowHideColumn, setIsShowHideColumn] = useState(false);

  const getRateplans = useCallback(async () => {
    try {
      const response = await getRatePlansByProperty(propertyId);
      setRateplans(
        response.data.data.map((item) => ({
          label: item.name,
          value: item.id, // Assuming `value` should be lowercase
        }))
      );
    } catch (error) {
      toast.error(`Failed to fetch Rateplans`);
    }
  }, [propertyId]);

  const getRoomtypes = useCallback(async () => {
    try {
      const response = await getRoomsByProperty(propertyId);
      setRoomtypes(
        response.data.data.map((item) => ({
          label: item.displayName,
          value: item.displayName,
        }))
      );
    } catch (error) {
      toast.error(`Failed to fetch Roomtypes`);
    }
  }, [propertyId]);

  useEffect(() => {
    getRateplans();
    getRoomtypes();
  }, [getRateplans, getRoomtypes]);

  const handleDropdownChange = (setPicker, key) => (value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
    setPicker(value);
  };

  const dropdownItem = (setData, key, item) => {
    return (
      <div
        title={item}
        className="rs-tag rs-tag-md rs-tag-default rs-tag-closable"
      >
        <span className="rs-tag-text">{item}</span>
        <button
          type="button"
          className="rs-tag-icon-close rs-btn-close"
          aria-label="Remove"
          tabIndex="-1"
          onClick={() => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              [key]: [],
            }));
            setData([]);
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1em"
            height="1em"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
            focusable="false"
            className="rs-icon"
            aria-label="close"
            data-category="application"
          >
            <path d="m2.784 2.089.069.058 5.146 5.147 5.146-5.147a.5.5 0 0 1 .765.638l-.058.069L8.705 8l5.147 5.146a.5.5 0 0 1-.638.765l-.069-.058-5.146-5.147-5.146 5.147a.5.5 0 0 1-.765-.638l.058-.069L7.293 8 2.146 2.854a.5.5 0 0 1 .638-.765"></path>
          </svg>
        </button>
      </div>
    );
  };

  const renderPickerValue = (setter, key, pluralLabel) => (value, items) => {
    const label =
      value.length > 1
        ? `${value.length} ${pluralLabel} selected`
        : items.map((item) => item.label).join(", ");
    return dropdownItem(setter, key, label);
  };

  const pickerConfigs = [
    {
      data: demandClients,
      placeholder: UI_LABELS.TRAVEL_BUYER,
      value: selectedDemandClients,
      onChange: handleDropdownChange(setSelectedDemandClients, "clients"),
      renderValue: renderPickerValue(
        setSelectedDemandClients,
        "clients",
        "clients"
      ),
    },
    {
      data: rateplans,
      placeholder: "Rate plan",
      value: selectedRateplans,
      onChange: handleDropdownChange(setSelectedRateplans, "rateplans"),
      renderValue: renderPickerValue(
        setSelectedRateplans,
        "rateplans",
        "rates"
      ),
    },
    {
      data: promoRatePlans,
      placeholder: "Promo Rate plan",
      value: selectedPromoRateplans,
      onChange: handleDropdownChange(
        setSelectedPromoRateplans,
        "promoRatePlans"
      ),
      renderValue: renderPickerValue(
        setSelectedPromoRateplans,
        "promoRatePlans",
        "rates"
      ),
    },
    {
      data: roomtypes,
      placeholder: "Room types",
      value: selectedRoomtypes,
      onChange: handleDropdownChange(setSelectedRoomtypes, "roomtypes"),
      renderValue: renderPickerValue(
        setSelectedRoomtypes,
        "roomtypes",
        "rooms"
      ),
    },
  ];

  return (
    <>
      <div className="filters">
        <span>
          <img className="icon" src={FilterIcon} alt="filter" />
          <span>Filter results</span>
        </span>
        {pickerConfigs.map((config) => (
          <CheckPicker
            key={config.placeholder}
            creatable
            data={config.data}
            className="dropdown"
            menuStyle={{ width: 220 }}
            placeholder={config.placeholder}
            value={config.value}
            onChange={config.onChange}
            renderValue={config.renderValue}
            preventOverflow
          />
        ))}
        <div className="button-section">
          <Button appearance="ghost" onClick={() => setIsShowHideColumn(true)}>
            Show / hide columns
          </Button>
          {isOffline ? (
            <Can permission={P.Reservations.PmsId.Upload}>
              <Button
                appearance="primary"
                onClick={() => setIsUploadSidePanelOpen(!isUploadSidePanelOpen)}
              >
                Upload PMS ID / CXL ID
              </Button>
            </Can>
          ) : (
            <Can permission={P.Reservations.Download}>
              <Button
                appearance="primary"
                loading={isExportLoading}
                onClick={() => exportReservation()}
              >
                Download Report
              </Button>
            </Can>
          )}
        </div>
      </div>
      <ReportShowHideColumnModal
        isOffline={isOffline}
        isShowHideColumn={isShowHideColumn}
        setIsShowHideColumn={setIsShowHideColumn}
        settingColumn={setColumns}
        timezoneSuffix={timeZoneId}
      />
    </>
  );
};

ResReportFilters.propTypes = {
  propertyId: PropTypes.number.isRequired,
  isUploadSidePanelOpen: PropTypes.bool.isRequired,
  isOffline: PropTypes.bool.isRequired,
  setColumns: PropTypes.func.isRequired,
  exportReservation: PropTypes.func.isRequired,
  isExportLoading: PropTypes.bool.isRequired,
  setIsUploadSidePanelOpen: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  timeZoneId: PropTypes.string.isRequired,
  demandClients: PropTypes.array.isRequired,
  promoRatePlans: PropTypes.array.isRequired,
  isPilotHotel: PropTypes.bool,
};

export default ResReportFilters;
